import qs from "qs";
import React from "react";
import PropTypes from "prop-types";
import { withRouter } from "react-router-dom";
import { observable } from "mobx";
import { observer } from "mobx-react";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import Skeleton from "@material-ui/lab/Skeleton";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Slider from "@material-ui/core/Slider";
import { withStyles } from "@material-ui/core/styles";
import CompareIcon from "@material-ui/icons/Compare";

import Scenario from "../../api/Scenario";
import Header from "../../components/utils/Header";
import KPI from "../../components/KPI";
import ScenarioMoreMenu from "./ScenarioMoreMenu";
import { chainAsyncCalls, debounce, formatRatioPercent } from "../../utils";
import {
  AvailabilityStats,
  CapacityStats,
  ProdVsDemandStats,
  RequiredActionsList,
  StockStats,
} from "./ScenarioComponents";
import { DemandIcon, DownloadIcon, SupplyIcon } from "../../theme/AthenaIcons";
import ScenarioRunReport from "./ScenarioRunReport";
import DataFrameDownload from "../../components/utils/DataFrameDownload";

const styles = (theme) => ({
  alert: {
    padding: theme.spacing(2),
    height: "100%",
  },
  greyFont: {
    color: "#727272",
  },
  title: {
    paddingTop: 50,
    paddingBottom: 20,
  },
  blueFont: {
    color: theme.palette.primary.main,
  },
  container: {
    padding: "0px 20px",
  },
  downloadBig: {
    border: "2px solid",
    borderColor: theme.palette.primary.main,
  },
});

@withRouter
@withStyles(styles)
@observer
class ScenarioDetailView extends React.Component {
  static propTypes = {
    instanceId: PropTypes.number.isRequired,
  };

  @observable availabilityThreshold = 0.95;
  @observable capacityMinThreshold = 0.95;
  @observable capacityMaxThreshold = 1.05;
  @observable prodVsDemandMinThreshold = 0.95;
  @observable prodVsDemandMaxThreshold = 1.05;
  @observable availabilityStats = null;
  @observable capacityStats = null;
  @observable prodVsDemandStats = null;
  @observable stockStats = null;

  constructor(props) {
    super(props);
    this.instance = Scenario.getInstance(props.instanceId);
  }

  onScenarioReady = () => {
    this.updateAvailabilityStats();
    this.updateCapacityStats();
    this.updateStockStats();
    this.updateProdVsDemandStats();
  };

  getAvailabilityPath(alerts = false, xlsx = false) {
    const params = {};
    if (alerts) {
      params["threshold"] = this.availabilityThreshold;
    }
    if (xlsx) {
      params["export"] = "xlsx";
    }
    return `/availability/?${qs.stringify(params)}`;
  }

  updateAvailabilityStats = debounce(
    chainAsyncCalls(async () => {
      this.availabilityStats = await this.instance.ajaxRequestData(
        "get",
        this.getAvailabilityPath(true)
      );
    }),
    500
  );

  onAvailabilityThresholdChange = (event, value) => {
    this.availabilityThreshold = value;
    this.availabilityStats = null;
    this.updateAvailabilityStats();
  };

  getCapacityPath(alerts = false, xlsx = false) {
    const params = {};
    if (alerts) {
      params["threshold_min"] = this.capacityMinThreshold;
      params["threshold_max"] = this.capacityMaxThreshold;
    }
    if (xlsx) {
      params["export"] = "xlsx";
    }
    return `/capacity/?${qs.stringify(params)}`;
  }

  getProdVsDemandPath(alerts = false, xlsx = false) {
    const params = {};
    if (alerts) {
      params["threshold_min"] = this.prodVsDemandMinThreshold;
      params["threshold_max"] = this.prodVsDemandMaxThreshold;
    }
    if (xlsx) {
      params["export"] = "xlsx";
    }
    return `/production_vs_demand/?${qs.stringify(params)}`;
  }

  updateCapacityStats = debounce(
    chainAsyncCalls(async () => {
      this.capacityStats = await this.instance.ajaxRequestData(
        "get",
        this.getCapacityPath(true)
      );
    }),
    500
  );

  updateProdVsDemandStats = debounce(
    chainAsyncCalls(async () => {
      this.prodVsDemandStats = await this.instance.ajaxRequestData(
        "get",
        this.getProdVsDemandPath(true)
      );
    }),
    500
  );

  onCapacityThresholdChange = (event, value) => {
    [this.capacityMinThreshold, this.capacityMaxThreshold] = value;
    this.capacityStats = null;
    this.updateCapacityStats();
  };

  onProdVsDemandThresholdChange = (event, value) => {
    [this.prodVsDemandMinThreshold, this.prodVsDemandMaxThreshold] = value;
    this.prodVsDemandStats = null;
    this.updateProdVsDemandStats();
  };

  getStockPath(alerts = false, xlsx = false) {
    const params = {};
    if (alerts) {
      params["alerts_only"] = "true";
    }
    if (xlsx) {
      params["export"] = "xlsx";
    }
    return `/stock/?${qs.stringify(params)}`;
  }

  updateStockStats = debounce(async () => {
    this.stockStats = await this.instance.ajaxRequestData(
      "get",
      this.getStockPath()
    );
  }, 500);

  get controls() {
    const { history } = this.props;
    return (
      <Grid container spacing={1} justify="flex-end" alignItems="center">
        <Grid item>
          <Button
            startIcon={
              <DemandIcon
                stroke="#00a8d7"
                transform="scale(1.2)"
                strokeWidth="3"
              />
            }
            variant="contained"
            component="a"
            href={`/demand/?scenario=${this.instance.id}`}
          >
            Demand
          </Button>
        </Grid>
        {this.instance.type !== 6 ? (
          <Grid item>
            <Button
              startIcon={
                <SupplyIcon
                  stroke="#00a8d7"
                  transform="scale(1.2)"
                  strokeWidth="3"
                />
              }
              variant="contained"
              component="a"
              href={`/supply/technology?scenario=${this.instance.id}`}
            >
              Technology
            </Button>
          </Grid>
        ) : (
          <></>
        )}

        <Grid item>
          <Button
            startIcon={<CompareIcon />}
            variant="contained"
            component="a"
            href={`/scenarios/comparison/?scenario1=${this.instance.id}`}
          >
            Compare
          </Button>
        </Grid>
        <Grid item>
          <DataFrameDownload
            href={Scenario.getApiUrl(`/${this.instance.id}/output_download/?`)}
            permission="athena.download_technology_dashboard"
          />
        </Grid>
        <Grid item>
          <ScenarioMoreMenu
            scenario={this.instance}
            onDelete={() => history.push("/scenarios/")}
          />
        </Grid>
      </Grid>
    );
  }

  get content() {
    if (!this.instance.isLoaded) {
      return <Skeleton variant="rect" height={550} />;
    }
    const { classes } = this.props;
    const getDownloadButton = (subPath, title) => (
      <Grid item>
        <Tooltip title={title} placement="top">
          <IconButton
            component="a"
            aria-label={title}
            href={this.instance.getApiUrl(subPath)}
          >
            <DownloadIcon />
          </IconButton>
        </Tooltip>
      </Grid>
    );
    const getDownloadButton2 = (subPath, title) => (
      <Paper className={classes.downloadBig}>
        <Grid
          container
          justify="space-between"
          alignItems="center"
          className={classes.container}
        >
          <Grid item>
            <Typography
              variant="h6"
              align="center"
              gutterBottom={false}
              className={classes.blueFont}
            >
              {title}
            </Typography>
          </Grid>
          <Grid item>
            <Tooltip title={`Download ${title}`} placement="top">
              <IconButton
                component="a"
                aria-label={title}
                href={this.instance.getApiUrl(subPath)}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>
    );
    return (
      <>
        <Grid item>
          <Typography variant="h6" className={classes.title}>
            KPI Overview
          </Typography>
        </Grid>
        <Grid item container spacing={2}>
          <Grid item container spacing={2}>
            <Grid item lg={3} md={6} xs={12}>
              <KPI {...this.instance.sales_kpi} type="big" />
            </Grid>
            <Grid item lg={3} md={6} xs={12}>
              <KPI {...this.instance.costs_kpi} type="big" />
            </Grid>
            <Grid item lg={3} md={6} xs={12}>
              <KPI {...this.instance.cash_locked_kpi} type="big" />
            </Grid>
            <Grid item lg={3} md={6} xs={12}>
              <KPI {...this.instance.prod_demand_kpi} type="big" />
            </Grid>
          </Grid>
          <Grid item container spacing={2}>
            <Grid item lg={3} md={6} xs={12}>
              <KPI {...this.instance.availability_fg_kpi} type="small" />
            </Grid>
            <Grid item lg={3} md={6} xs={12}>
              <KPI {...this.instance.capacity_fg_kpi} type="small" />
            </Grid>
            <Grid item lg={3} md={6} xs={12}>
              <KPI {...this.instance.stock_fg_kpi} type="small" />
            </Grid>
            <Grid item lg={3} md={6} xs={12}>
              <KPI {...this.instance.fg_prod_demand_kpi} type="small" />
            </Grid>
            <Grid item lg={3} md={6} xs={12} />
            <Grid item lg={3} md={6} xs={12}>
              <KPI {...this.instance.capacity_sfg_kpi} type="small" />
            </Grid>
            <Grid item lg={3} md={6} xs={12}>
              <KPI {...this.instance.stock_sfg_kpi} type="small" />
            </Grid>
            <Grid item lg={3} md={6} xs={12}>
              <KPI {...this.instance.sfg_prod_demand_kpi} type="small" />
            </Grid>
            <Grid item lg={3} md={6} xs={12} />
            <Grid item lg={3} md={6} xs={12}>
              <KPI
                {...this.instance.active_vs_available_capacity_fg_kpi}
                type="small"
              />
            </Grid>
            <Grid item lg={3} md={6} xs={12} />
            <Grid item lg={3} md={6} xs={12} />
            <Grid item lg={3} md={6} xs={12} />
            <Grid item lg={3} md={6} xs={12}>
              <KPI
                {...this.instance.active_vs_available_capacity_sfg_kpi}
                type="small"
              />
            </Grid>
            <Grid item lg={3} md={6} xs={12} />
            <Grid item lg={3} md={6} xs={12} />
            <Grid item lg={3} md={6} xs={12}>
              {getDownloadButton2(
                this.getAvailabilityPath(false, true),
                "Sales Coverage Detail"
              )}
            </Grid>
            <Grid item lg={3} md={6} xs={12}>
              {getDownloadButton2(
                this.getCapacityPath(false, true),
                "Capacity Detail"
              )}
            </Grid>
            <Grid item lg={3} md={6} xs={12}>
              {getDownloadButton2(
                this.getStockPath(false, true),
                "Stock Detail"
              )}
            </Grid>
            <Grid item lg={3} md={6} xs={12}>
              {getDownloadButton2(
                this.getProdVsDemandPath(false, true),
                "Prod Vs Demand Detail"
              )}
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <Typography variant="h6" className={classes.title}>
            Alerts
          </Typography>
        </Grid>
        <Grid
          container
          item
          justify="space-evenly"
          spacing={2}
          alignItems="stretch"
        >
          <Grid container item xs={12} md={6} lg={3} direction="column">
            <Paper className={classes.alert}>
              <Grid container direction="column" justify="space-between">
                <Grid item>
                  <Grid
                    item
                    container
                    justify="space-between"
                    alignItems="center"
                  >
                    <Grid item>
                      <Typography
                        variant="h6"
                        align="center"
                        gutterBottom={false}
                        className={classes.blueFont}
                      >
                        Sales Coverage
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Grid container justify="flex-end">
                        {getDownloadButton(
                          this.getAvailabilityPath(true, true),
                          "Download Alerts List"
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid container item>
                    {this.availabilityStats === null ? (
                      <Skeleton variant="rect" width="100%" height={82} />
                    ) : (
                      <AvailabilityStats
                        {...this.availabilityStats}
                        threshold={this.availabilityThreshold}
                      />
                    )}
                  </Grid>
                </Grid>
                <Grid item>
                  <Grid container item>
                    <Grid container item justify="space-between">
                      <Grid item>
                        <Typography
                          id="availability-threshold"
                          gutterBottom
                          className={classes.greyFont}
                        >
                          Alert threshold
                        </Typography>
                      </Grid>
                      <Grid item>
                        <span className={classes.greyFont}>
                          {formatRatioPercent(this.availabilityThreshold, 0)}
                        </span>
                      </Grid>
                    </Grid>
                    <Slider
                      value={this.availabilityThreshold}
                      onChange={this.onAvailabilityThresholdChange}
                      max={1}
                      step={0.01}
                      valueLabelDisplay="auto"
                      valueLabelFormat={formatRatioPercent}
                      aria-labelledby="availability-threshold"
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          <Grid container item xs={12} md={6} lg={3} direction="column">
            <Paper className={classes.alert}>
              <Grid container direction="column" justify="space-between">
                <Grid item>
                  <Grid
                    item
                    container
                    justify="space-between"
                    alignItems="center"
                  >
                    <Grid item>
                      <Typography
                        variant="h6"
                        align="center"
                        gutterBottom={false}
                        className={classes.blueFont}
                      >
                        Capacity utilization
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Grid container justify="flex-end">
                        {getDownloadButton(
                          this.getCapacityPath(true, true),
                          "Download Alerts List"
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid container item>
                    {this.capacityStats === null ? (
                      <Skeleton variant="rect" width="100%" height={82} />
                    ) : (
                      <CapacityStats
                        {...this.capacityStats}
                        minThreshold={this.capacityMinThreshold}
                        maxThreshold={this.capacityMaxThreshold}
                      />
                    )}
                  </Grid>
                </Grid>
                <Grid item>
                  <Grid container item>
                    <Grid container item justify="space-between">
                      <Grid item>
                        <Typography
                          id="availability-threshold"
                          gutterBottom
                          className={classes.greyFont}
                        >
                          Alert threshold
                        </Typography>
                      </Grid>
                      <Grid item>
                        <span className={classes.greyFont}>
                          {`${formatRatioPercent(
                            this.capacityMinThreshold,
                            0
                          )} - ${formatRatioPercent(
                            this.capacityMaxThreshold,
                            0
                          )}`}
                        </span>
                      </Grid>
                    </Grid>
                    <Slider
                      value={[
                        this.capacityMinThreshold,
                        this.capacityMaxThreshold,
                      ]}
                      onChange={this.onCapacityThresholdChange}
                      max={2}
                      step={0.01}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => formatRatioPercent(value, 0)}
                      track="inverted"
                      aria-labelledby="capacity-threshold"
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          <Grid container item xs={12} md={6} lg={3} direction="column">
            <Paper className={classes.alert}>
              <Grid item container justify="space-between" alignItems="center">
                <Grid item>
                  <Typography
                    variant="h6"
                    align="center"
                    gutterBottom={false}
                    className={classes.blueFont}
                  >
                    Stock projection
                  </Typography>
                </Grid>
                <Grid item>
                  <Grid container justify="flex-end">
                    {getDownloadButton(
                      this.getStockPath(true, true),
                      "Download Alerts List"
                    )}
                  </Grid>
                </Grid>
              </Grid>
              <Grid container item>
                {this.stockStats === null ? (
                  <Skeleton variant="rect" width="100%" height={170} />
                ) : (
                  <StockStats {...this.stockStats} showMin showMax />
                )}
              </Grid>
            </Paper>
          </Grid>
          <Grid container item xs={12} md={6} lg={3} direction="column">
            <Paper className={classes.alert}>
              <Grid container direction="column" justify="space-between">
                <Grid item>
                  <Grid
                    item
                    container
                    justify="space-between"
                    alignItems="center"
                  >
                    <Grid item>
                      <Typography
                        variant="h6"
                        align="center"
                        gutterBottom={false}
                        className={classes.blueFont}
                      >
                        Production Vs. Demand
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Grid container justify="flex-end">
                        {getDownloadButton(
                          this.getProdVsDemandPath(true, true),
                          "Download Alerts List"
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid container item>
                    {this.prodVsDemandStats === null ? (
                      <Skeleton variant="rect" width="100%" height={82} />
                    ) : (
                      <ProdVsDemandStats
                        {...this.prodVsDemandStats}
                        minThreshold={this.prodVsDemandMinThreshold}
                        maxThreshold={this.prodVsDemandMaxThreshold}
                      />
                    )}
                  </Grid>
                </Grid>
                <Grid item>
                  <Grid container item>
                    <Grid container item justify="space-between">
                      <Grid item>
                        <Typography
                          id="availability-threshold"
                          gutterBottom
                          className={classes.greyFont}
                        >
                          Alert threshold
                        </Typography>
                      </Grid>
                      <Grid item>
                        <span className={classes.greyFont}>
                          {`${formatRatioPercent(
                            this.prodVsDemandMinThreshold,
                            0
                          )} - ${formatRatioPercent(
                            this.prodVsDemandMaxThreshold,
                            0
                          )}`}
                        </span>
                      </Grid>
                    </Grid>
                    <Slider
                      value={[
                        this.prodVsDemandMinThreshold,
                        this.prodVsDemandMaxThreshold,
                      ]}
                      onChange={this.onProdVsDemandThresholdChange}
                      max={2}
                      step={0.01}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => formatRatioPercent(value, 0)}
                      track="inverted"
                      aria-labelledby="prod-vs-demand-threshold"
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
        <Grid item>
          <Typography variant="h6" className={classes.title}>
            Required Actions
          </Typography>
        </Grid>
        <Grid item container spacing={2}>
          <Grid item>
            <RequiredActionsList items={[...this.instance.required_actions]} />
          </Grid>
        </Grid>
      </>
    );
  }

  render() {
    if (!this.instance.isSuccess) {
      return (
        <ScenarioRunReport
          instanceId={this.props.instanceId}
          onScenarioReady={this.onScenarioReady}
        />
      );
    }
    return (
      <Grid container direction="column" spacing={4} wrap="nowrap">
        <Grid
          item
          container
          spacing={1}
          justify="space-between"
          alignItems="center"
          wrap="nowrap"
        >
          <Grid item>
            <Header title={this.instance.toString()} />
          </Grid>
          <Grid item>{this.controls}</Grid>
        </Grid>
        <Grid item container>
          {this.content}
        </Grid>
      </Grid>
    );
  }
}

export default ScenarioDetailView;
